import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Device, DeviceList } from '@/components/dashboard/device-list';
import { AlertsList } from '@/components/dashboard/alerts-list';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Laptop, BarChart, Signal, Database, AlertTriangle, Clock } from 'lucide-react';
import { useGetAnalyticsDashboardQuery, useGetDevicesQuery } from '@/services';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatsCard } from '@/components/dashboard/stats-card';
import { SensorDistributionChart } from '@/components/dashboard/sensor-distribution-chart';
import { ReadingTrendsChart } from '@/components/dashboard/reading-trends-chart';

export const Route = createFileRoute('/_authenticated/(user-dashboard)/dashboard/_dashboard/')({
  component: DashboardPage,
});

function DashboardPage() {
  const [timeRange, setTimeRange] = useState('7');

  // Fetch dashboard stats from API
  const { data: dashboardData, isLoading } = useGetAnalyticsDashboardQuery({
    days: timeRange,
  });

  // Fetch devices list
  const { data: devicesData } = useGetDevicesQuery({});

  // Extract stats from the API response
  const stats = dashboardData?.data || {
    total_devices: 0,
    active_devices: 0,
    total_sensors: 0,
    active_sensors: 0,
    readings_last_24h: 0,
    recent_alerts: 0,
    sensor_type_distribution: [],
    reading_trends: [],
  };

  // Use actual device data if available
  const devices: Device[] =
    devicesData?.data?.map((device) => ({
      id: device.id,
      name: device.name || `Device ${device.id.substring(0, 8)}`,
      status: device.status || 'offline',
      lastSeen: device.last_reading_at || device.created_at || new Date().toISOString(),
      location: device.location_name || 'Unknown',
    })) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-4 sm:p-6 space-y-6">
      {/* Header Section with Gradient */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-2xl"></div>
        <div className="relative bg-background/80 backdrop-blur-sm border border-border/50 rounded-2xl p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Dashboard Overview
              </h1>
              <p className="text-muted-foreground mt-1">Monitor your IoT devices and sensors in real-time</p>
            </div>

            {/* Time range selector with improved styling */}
            <div className="flex items-center gap-3 bg-muted/50 rounded-full px-4 py-2 border border-border/50">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Select defaultValue={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-[140px] border-none bg-transparent focus:ring-0">
                  <SelectValue placeholder="Time period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards Grid with enhanced styling */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <StatsCard title="Total Devices" value={stats.total_devices} icon={Laptop} loading={isLoading} />
        <StatsCard
          title="Active Devices"
          value={stats.active_devices}
          icon={Signal}
          loading={isLoading}
          description={`${Math.round((stats.active_devices / (stats.total_devices || 1)) * 100)}% online`}
        />
        <StatsCard title="Total Sensors" value={stats.total_sensors} icon={Database} loading={isLoading} />
        <StatsCard
          title="Recent Readings"
          value={stats.readings_last_24h.toLocaleString()}
          icon={BarChart}
          loading={isLoading}
          description="In the last 24 hours"
        />
      </div>

      {/* Enhanced Charts Grid with better responsive layout */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-3 xl:grid-cols-3">
        {/* Sensor Distribution - Full width on mobile, 1/3 on large screens */}
        <div className="lg:col-span-1">
          <SensorDistributionChart data={stats.sensor_type_distribution} loading={isLoading} />
        </div>

        {/* Reading Trends - Full width on mobile, 1/3 on large screens */}
        <div className="lg:col-span-1">
          <ReadingTrendsChart data={stats.reading_trends} loading={isLoading} />
        </div>

        {/* Recent Alerts - Full width on mobile, 1/3 on large screens */}
        <div className="lg:col-span-1">
          <Card className="h-full border-border/50 shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                Recent Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AlertsList limit={5} showAll={true} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Devices Panel with improved layout */}
      <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow duration-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <Laptop className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            Recent Devices
            <span className="ml-auto text-sm text-muted-foreground font-normal">{devices.length} devices</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DeviceList devices={devices.slice(0, 5)} showViewAll={true} />
        </CardContent>
      </Card>
    </div>
  );
}
